/** Unified appointment form for creating and editing appointments. */

import { useStore } from '@tanstack/react-form'
import { Loader2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import z from 'zod'

import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPES_BY_ID,
  type Appointment,
  type AppointmentWithRelations,
  appointmentTypeIdSchema,
  clinicIdSchema,
  formatName,
  isAppointmentTypeAvailableAtClinic,
  isAppointmentTypeId,
  toWallClockDate,
  toWallClockTime,
  type WithNullable,
  type WithOptional,
  wallClockToUtcIso,
} from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { useAppointmentWarnings } from '@/frontend/features/appointments/use-appointment-warnings'
import { PatientSearch } from '@/frontend/features/patient/patient-search'
import { useCreateAppointment } from '@/frontend/hooks/api/use-create-appointment'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'
import { usePatchAppointment } from '@/frontend/hooks/api/use-patch-appointment'

const appointmentFormSchema = z
  .object({
    patientId: z.string().min(1, 'Please select a patient'),
    appointmentTypeId: appointmentTypeIdSchema,
    clinicId: z.enum(clinicIdSchema.options, { message: 'Please select a clinic' }),
    staffId: z.string().min(1, 'Please select a vet'),
    date: z.string().min(1, 'Please select a date'),
    startTime: z.string().min(1, 'Please select a start time'),
    endTime: z.string().min(1, 'Please select an end time'),
    visitReason: z.string().nullish(),
    userNotes: z.string().nullish(),
    notifyMember: z.boolean().default(true),
  })
  .refine((v) => `${v.date}T${v.endTime}` > `${v.date}T${v.startTime}`, {
    message: 'End time must be after start time',
    path: ['endTime'],
  })

// The schema is the strict, submittable contract; the form's live state loosens
// clinicId to null so the picker's "no selection" binds without a cast.
export type AppointmentFormValues = WithNullable<z.infer<typeof appointmentFormSchema>, 'clinicId'>

/** Converts an appointment entity to form default values. */
export function appointmentToFormValues(
  appt: Appointment,
  timeZone: string
): Partial<AppointmentFormValues> {
  const start = new Date(appt.startTime)
  const end = new Date(appt.endTime)
  return {
    patientId: appt.patientId,
    // A Digitail-era type leaves the field empty, so an edit has to pick a current type.
    appointmentTypeId: isAppointmentTypeId(appt.appointmentTypeId)
      ? appt.appointmentTypeId
      : undefined,
    clinicId: appt.clinicId ?? null,
    staffId: appt.staffId,
    date: toWallClockDate(start, timeZone),
    startTime: toWallClockTime(start, timeZone),
    endTime: toWallClockTime(end, timeZone),
    visitReason: appt.visitReason,
    userNotes: appt.userNotes,
    notifyMember: true,
  }
}

interface AppointmentFormProps {
  defaultValues: Partial<AppointmentFormValues>
  /** IANA timezone used to display and convert wall-clock date/time values. */
  timeZone: string
  /** When provided the form operates in edit mode (PATCH). Otherwise it creates (POST). */
  appointmentId?: string
  /** Display-only patient context (e.g. when editing an existing appointment). */
  patientDetails?: { name: string; owner?: { firstName: string; lastName: string } }
  onSuccess?: (appointment: AppointmentWithRelations) => void
  onCancel?: () => void
}

export function AppointmentForm({
  defaultValues,
  timeZone,
  appointmentId,
  patientDetails,
  onSuccess,
  onCancel,
}: AppointmentFormProps) {
  const { mutateAsync: createAppointment } = useCreateAppointment()
  const { mutateAsync: patchAppointment } = usePatchAppointment()
  const { data: staff } = useListStaff()

  const isEditing = appointmentId !== undefined
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const form = useAppForm({
    // StandardSchema doesn't handle .default() very well, so seed notifyMember
    // here to ensure the checkbox starts checked for fresh bookings too.
    defaultValues: { notifyMember: true, ...defaultValues } as WithOptional<
      AppointmentFormValues,
      'notifyMember'
    >,
    validators: {
      onSubmit: appointmentFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      // The onSubmit validator already ran, so re-parsing recovers the strict
      // types it guaranteed (notably a non-null clinicId) without a manual guard.
      const parsed = appointmentFormSchema.parse(value)
      try {
        const startIso = wallClockToUtcIso(parsed.date, parsed.startTime, timeZone)
        const endIso = wallClockToUtcIso(parsed.date, parsed.endTime, timeZone)

        const { appointment } = isEditing
          ? await patchAppointment({
              id: appointmentId,
              startTime: startIso,
              endTime: endIso,
              staffId: parsed.staffId,
              clinicId: parsed.clinicId,
              appointmentTypeId: parsed.appointmentTypeId,
              visitReason: parsed.visitReason || null, // explicitly set to null if empty
              userNotes: parsed.userNotes || null, // explicitly set to null if empty
              notifyMember: parsed.notifyMember,
            })
          : await createAppointment({
              patientId: parsed.patientId,
              appointmentTypeId: parsed.appointmentTypeId,
              clinicId: parsed.clinicId,
              staffId: parsed.staffId,
              startTime: startIso,
              endTime: endIso,
              visitReason: parsed.visitReason,
              userNotes: parsed.userNotes,
              notifyMember: parsed.notifyMember,
            })

        if (!isEditing) form.reset()
        onSuccess?.(appointment)
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save appointment',
            fields: {},
          },
        })
      }
    },
  })

  const {
    patientId,
    staffId,
    clinicId: formClinicId,
    date,
    startTime,
    endTime,
    appointmentTypeId,
  } = useStore(form.store, (s) => s.values)

  const {
    warnings,
    isLoading: warningsLoading,
    isError: warningsError,
  } = useAppointmentWarnings(
    {
      patientId: patientId || undefined,
      staffId: staffId || undefined,
      clinicId: formClinicId || undefined,
      date: date || undefined,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      appointmentTypeId: appointmentTypeId || undefined,
      timeZone,
      excludeAppointmentId: appointmentId,
    },
    { isNewAppointment: !isEditing }
  )
  const selectedStaff = staff?.find((member) => member.id === staffId)

  // Hide types whose `allowedClinicIds` excludes the currently selected clinic so staff can't
  // pick a combination the backend would reject.
  const appointmentTypeItems = APPOINTMENT_TYPES.filter((t) =>
    formClinicId ? isAppointmentTypeAvailableAtClinic(t.id, formClinicId) : true
  )
    .map((t) => ({ label: t.name, value: t.id }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (warningsLoading) return
        if (warnings.length > 0) {
          setConfirmDialogOpen(true)
          return
        }
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-3 py-1">
          <form.GlobalError />

          <form.Field name="patientId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Patient"
                render={({ id, disabled }) => (
                  <>
                    {patientDetails && (
                      <div className="flex items-center gap-x-2">
                        {patientDetails.name}
                        {patientDetails.owner && (
                          <span className="text-muted-foreground">
                            ({formatName(patientDetails.owner)})
                          </span>
                        )}
                      </div>
                    )}
                    {!defaultValues.patientId && (
                      <PatientSearch
                        id={id}
                        aria-invalid={!!field.state.meta.errors.length}
                        onChange={(id) => field.handleChange(id ?? '')}
                        onBlur={field.handleBlur}
                        disabled={disabled}
                      />
                    )}
                  </>
                )}
              />
            )}
          </form.Field>

          <div className="grid grid-cols-3 gap-3">
            <form.AppField
              name="appointmentTypeId"
              listeners={{
                onChange: ({ value }) => {
                  const type = APPOINTMENT_TYPES_BY_ID[value]
                  if (!type) return
                  const currentStart = form.getFieldValue('startTime')
                  if (!currentStart) return
                  const [h, m] = currentStart.split(':').map(Number)
                  const endMinutes = h * 60 + m + type.duration
                  const endH = Math.floor(endMinutes / 60)
                  const endM = endMinutes % 60
                  form.setFieldValue(
                    'endTime',
                    `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`
                  )
                },
              }}
            >
              {(field) => (
                <field.SelectField
                  label="Appointment type"
                  placeholder="Select type"
                  items={appointmentTypeItems}
                />
              )}
            </form.AppField>
            <form.AppField
              name="clinicId"
              listeners={{
                onChange: ({ value }) => {
                  // If switching clinic invalidates the currently selected appointment type,
                  // clear the appointment type
                  const currentType = form.getFieldValue('appointmentTypeId')
                  if (!currentType || !value) return
                  if (isAppointmentTypeAvailableAtClinic(currentType, value)) return
                  // @ts-expect-error internal form state doesn't respect null, but we *want* to invoke an invalid state
                  form.setFieldValue('appointmentTypeId', null)
                },
              }}
            >
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Clinic"
                  render={({ id, disabled }) => (
                    <ClinicSearch
                      id={id}
                      value={field.state.value}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled}
                      placeholder="Select clinic"
                      aria-invalid={!!field.state.meta.errors.length}
                    />
                  )}
                />
              )}
            </form.AppField>
            <form.Field name="staffId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Assigned vet"
                  render={({ id, disabled }) => (
                    <StaffSearch
                      id={id}
                      value={field.state.value}
                      onChange={(staff) => field.handleChange(staff?.id ?? '')}
                      onBlur={field.handleBlur}
                      placeholder="Select vet"
                      disabled={disabled}
                      aria-invalid={!!field.state.meta.errors.length}
                    />
                  )}
                />
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <form.AppField name="date">
              {(field) => <field.InputField label="Date" type="date" />}
            </form.AppField>
            <form.AppField name="startTime">
              {(field) => <field.InputField label="Start time" type="time" />}
            </form.AppField>
            <form.AppField name="endTime">
              {(field) => <field.InputField label="End time" type="time" />}
            </form.AppField>
          </div>

          <form.AppField name="visitReason">
            {(field) => (
              <field.TextareaField
                label="Visit reason"
                description="Context for other Snoots staff. Not shared with the member."
                placeholder="e.g. member says they'll have AHC questionnaire completed by 14th Jun"
                rows={2}
              />
            )}
          </form.AppField>

          <form.AppField name="userNotes">
            {(field) => (
              <field.TextareaField
                label="Member notes"
                description="Anything the member wants the vet to know. Visible to the member."
                placeholder="e.g. Biscuit has been off her food for a few days"
                rows={2}
              />
            )}
          </form.AppField>

          <form.AppField name="notifyMember">
            {(field) => <field.CheckboxField label="Notify pet owner by email" />}
          </form.AppField>
        </div>

        {(warningsError || warningsLoading || warnings.length > 0) && (
          <div className="mt-4 space-y-2">
            {warnings.length > 0 && (
              <div className="space-y-2">
                {warnings.slice(0, 3).map((warning, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: derived array, never reordered
                  <Alert key={i} variant="warning">
                    <TriangleAlert />
                    <AlertTitle>{warning.title}</AlertTitle>
                    {warning.description && (
                      <AlertDescription>{warning.description}</AlertDescription>
                    )}
                  </Alert>
                ))}
                {warnings.length > 3 && (
                  <p className="text-sm text-muted-foreground pl-1">
                    + {warnings.length - 3} more warning{warnings.length - 3 > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {warningsLoading ? (
              <p
                className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-3"
                role="status"
              >
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Checking appointment validity...
              </p>
            ) : null}

            {warningsError ? (
              <Alert variant="destructive">
                <TriangleAlert />
                <AlertTitle>Some appointment checks couldn&apos;t be completed</AlertTitle>
                <AlertDescription>
                  We couldn't complete all checks on this appointment. You can still book or
                  reschedule this appointment.
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}

        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                Please review the following warnings before booking into the selected slot:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-sm">
              <dt className="text-xs font-semibold text-muted-foreground">Vet</dt>
              <dd>{selectedStaff ? formatName(selectedStaff) : staffId}</dd>
              <dt className="text-xs font-semibold text-muted-foreground">Date</dt>
              <dd>{date}</dd>
              <dt className="text-xs font-semibold text-muted-foreground">Time</dt>
              <dd>
                {startTime} - {endTime}
              </dd>
            </dl>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {warnings.map((warning, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: derived array, never reordered
                <Alert key={i} variant="warning">
                  <TriangleAlert />
                  <AlertTitle>{warning.title}</AlertTitle>
                  {warning.description && (
                    <AlertDescription>{warning.description}</AlertDescription>
                  )}
                </Alert>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => form.handleSubmit()}>
                {isEditing ? 'Save anyway' : 'Create anyway'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="flex justify-end gap-2 pt-3 mt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting || warningsLoading}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save changes' : 'Create appointment'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
